import { LayoutMode } from '@lved/shared-core';
import { Command } from '@theia/core';
import {
    ApplicationShell,
    FrontendApplicationContribution,
    KeybindingContribution,
    KeybindingRegistry
} from '@theia/core/lib/browser';
import { CommandContribution, CommandRegistry } from '@theia/core/lib/common/command';
import { inject, injectable } from '@theia/core/shared/inversify';

const DESIGN_PANEL_WIDGET_ID: string = 'widget:lvgl-design-panel';
const PREVIEW_WIDGET_ID: string = 'widget:lvgl-preview';
const TERMINAL_MANAGER_MINIMIZE_BOTTOM_PANEL_COMMAND: Command = {
    id: 'terminal:manager-minimize-bottom-panel'
};

// On some keyboard layouts Ctrl/Cmd+Shift+0 resolves to Theia's terminal toggle/new shortcut.
const TERMINAL_CONFLICTING_SHORTCUT_COMMANDS: readonly Command[] = [
    { id: 'terminal:new' },
    { id: 'terminal:new:active:workspace' },
    { id: 'workbench.action.terminal.toggleTerminal' }
];

type DesignShortcutAction = 'cut' | 'copy' | 'paste';
type DesignShortcutCommand = {
    command: Command;
    keybinding: string;
    action: DesignShortcutAction;
};
type PreviewZoomCommand = {
    command: Command;
    keybindings: readonly string[];
    eventId: string;
};
type UiShortcutModule = {
    dispatchDesignShortcut: (action: DesignShortcutAction) => void;
};

const DESIGN_SHORTCUT_COMMANDS: readonly DesignShortcutCommand[] = [
    {
        command: { id: 'lvgl.design.cut', label: 'Cut Layer' },
        keybinding: 'ctrlcmd+x',
        action: 'cut'
    },
    {
        command: { id: 'lvgl.design.copy', label: 'Copy Layer' },
        keybinding: 'ctrlcmd+c',
        action: 'copy'
    },
    {
        command: { id: 'lvgl.design.paste', label: 'Paste Layer' },
        keybinding: 'ctrlcmd+v',
        action: 'paste'
    }
];

const PREVIEW_ZOOM_COMMANDS: readonly PreviewZoomCommand[] = [
    {
        command: { id: 'lvgl.preview.zoomIn', label: 'Zoom In' },
        keybindings: ['ctrlcmd+=', 'ctrlcmd+shift+=', 'ctrlcmd+up'],
        eventId: 'zoom-in'
    },
    {
        command: { id: 'lvgl.preview.zoomOut', label: 'Zoom Out' },
        keybindings: ['ctrlcmd+-', 'ctrlcmd+down'],
        eventId: 'zoom-out'
    },
    {
        command: { id: 'lvgl.preview.zoomReset', label: 'Reset Zoom' },
        keybindings: ['ctrlcmd+0'],
        eventId: 'zoom-reset'
    },
    {
        command: { id: 'lvgl.preview.fitToScreen', label: 'Fit to Screen' },
        keybindings: ['ctrlcmd+shift+0'],
        eventId: 'fit-to-screen'
    }
];

@injectable()
export class KeybindingOverridesContribution
    implements FrontendApplicationContribution, KeybindingContribution, CommandContribution
{
    @inject(KeybindingRegistry)
    protected readonly keybindings!: KeybindingRegistry;

    @inject(ApplicationShell)
    protected readonly shell!: ApplicationShell;

    registerCommands(commands: CommandRegistry): void {
        for (const shortcut of DESIGN_SHORTCUT_COMMANDS) {
            commands.registerCommand(shortcut.command, {
                execute: () => this.executeDesignShortcut(shortcut.action),
                isEnabled: () => this.canHandleDesignShortcut()
            });
        }

        for (const shortcut of PREVIEW_ZOOM_COMMANDS) {
            commands.registerCommand(shortcut.command, {
                execute: () => this.sendPreviewEvent(shortcut.eventId),
                isEnabled: () => this.canHandlePreviewShortcut()
            });
        }
    }

    registerKeybindings(keybindings: KeybindingRegistry): void {
        keybindings.registerKeybinding({
            command: `-${TERMINAL_MANAGER_MINIMIZE_BOTTOM_PANEL_COMMAND.id}`,
            keybinding: 'alt+q'
        });

        for (const shortcut of DESIGN_SHORTCUT_COMMANDS) {
            keybindings.registerKeybinding({
                command: shortcut.command.id,
                keybinding: shortcut.keybinding
            });
        }

        for (const shortcut of PREVIEW_ZOOM_COMMANDS) {
            for (const keybinding of shortcut.keybindings) {
                keybindings.registerKeybinding({
                    command: shortcut.command.id,
                    keybinding
                });
            }
        }
    }

    onStart(): void {
        this.keybindings.unregisterKeybinding(TERMINAL_MANAGER_MINIMIZE_BOTTOM_PANEL_COMMAND);
        for (const command of TERMINAL_CONFLICTING_SHORTCUT_COMMANDS) {
            this.keybindings.unregisterKeybinding(command);
        }
    }

    protected async executeDesignShortcut(action: DesignShortcutAction): Promise<void> {
        if (!this.canHandleDesignShortcut()) return;

        const uiModule: UiShortcutModule = (await import('@lved/ui')) as unknown as UiShortcutModule;
        uiModule.dispatchDesignShortcut(action);
    }

    protected sendPreviewEvent(eventId: string): void {
        if (!this.canHandlePreviewShortcut()) return;
        window._platformApi?.sendGlobalEvent(eventId);
    }

    protected canHandleDesignShortcut(): boolean {
        return this.isDesignShortcutContext() && !this.isEditableElementActive();
    }

    protected canHandlePreviewShortcut(): boolean {
        return this.isPreviewShortcutContext() && !this.isEditableElementActive();
    }

    protected isDesignMode(): boolean {
        return document.body.dataset.lvglLayout === LayoutMode.DESIGN;
    }

    protected isDesignShortcutContext(): boolean {
        return this.isDesignMode() || this.isShellWidgetActive(DESIGN_PANEL_WIDGET_ID);
    }

    protected isPreviewShortcutContext(): boolean {
        return (
            this.isDesignMode() ||
            this.isShellWidgetActive(DESIGN_PANEL_WIDGET_ID) ||
            this.isShellWidgetActive(PREVIEW_WIDGET_ID)
        );
    }

    protected isShellWidgetActive(widgetId: string): boolean {
        return this.shell.activeWidget?.id === widgetId || this.shell.currentWidget?.id === widgetId;
    }

    protected isEditableElementActive(): boolean {
        const activeElement: Element | null = document.activeElement;
        if (!(activeElement instanceof HTMLElement)) return false;

        return (
            activeElement instanceof HTMLInputElement ||
            activeElement instanceof HTMLTextAreaElement ||
            activeElement instanceof HTMLSelectElement ||
            activeElement.isContentEditable ||
            Boolean(activeElement.closest('[contenteditable="true"]'))
        );
    }
}
